class ApiResourceBuilder {
  static FilterOPs = {
    EQ: "EQ",
    LT: "LT",
    LTE: "LTE",
    GT: "GT",
    GTE: "GTE",
    NOT: "NOT",
    LIKE: "LIKE",
    IN: "IN",
    NIN: "NIN"
  };

  static ConnectionOPs = {
    AND: "AND",
    OR: "OR"
  };

  static OrderOPs = {
    ASC: "ASC",
    DESC: "DESC"
  };

  static TransformOPs = {
    AVG: "AVG",
    SUM: "SUM"
  };

  static GroupOPs = {
    SUM: "SUM",
    COUNT: "COUNT",
    LAST: "LAST",
    MAX: "MAX",
    MIN: "MIN",
    FIRST: "FIRST",
    AVG: "AVG"
  };

  static buildFromParams(
    resource,
    { filters, order, pagination, transforms, group },
    additionalParams = []
  ) {
    const builder = new ApiResourceBuilder().resource(resource);

    if (!!filters) {
      filters.forEach(({ field, value, operation, connector = false }) => {
        if (!!connector) {
          builder.addFilter(field, value, operation, connector);
        } else {
          builder.addFilter(field, value, operation);
        }
      });
    }

    if (!!transforms) {
      transforms.forEach(({ operation, field }) => {
        builder.addTransformation(operation, field);
      });
    }

    if (!!order) {
      order.forEach(({ field, direction }) => {
        builder.order(field, direction);
      });
    }

    if (!!pagination) {
      if (!!pagination.page) {
        builder.page(pagination.page);
      }

      if (!!pagination.perPage) {
        builder.perPage(pagination.perPage);
      }
    }

    if (!!group) {
      builder.group(group.field, group.transformations);
    }

    return builder.build(additionalParams);
  }

  constructor() {
    this.builderParams = {
      resource: null,
      page: null,
      perPage: null,
      orderParams: [],
      transformationParams: [],
      filterParams: [],
      groupParams: false
    };
  }

  buildResource = (builderParams, additionalParams = []) => {
    const hasFilterParams = builderParams.filterParams.length > 0;
    const hasTransformParams = builderParams.transformationParams.length > 0;
    const hasOrderParams = builderParams.orderParams.length > 0;
    const hasGroupParams = !!builderParams.groupParams;

    const query = additionalParams;

    if (hasFilterParams) {
      const formatValue = value => {
        return !!value ? value.toString().replace(/%/g, "%25") : "";
      };
      const filterMapper = ({ field, value, operation, connector }) =>
        `${connector.toLowerCase()}:${field}:${operation.toLowerCase()}:${formatValue(
          value
        )}`;
      const filterString = builderParams.filterParams
        .map(filterMapper)
        .join(",");
      query.push(`filter=${filterString}`);
    }
    if (hasGroupParams) {
      const transformationsMapper = ({ field, operation }) =>
        `${field}:${operation.toLowerCase()}`;
      const transformations = builderParams.groupParams.transformations.map(
        transformationsMapper
      );
      const queryPart = [
        `group=${builderParams.groupParams.field}`,
        ...transformations
      ].join(",");
      query.push(queryPart);
    }
    if (hasTransformParams) {
      const transformMapper = ({ operation, field }) =>
        `${operation.toLowerCase()}:${field}`;
      const transformString = builderParams.transformationParams
        .map(transformMapper)
        .join(",");
      query.push(`transform=${transformString}`);
    } else {
      if (hasOrderParams) {
        const orderMapper = ({ field, direction }) => {
          const apiDirection =
            direction === ApiResourceBuilder.OrderOPs.ASC ? "" : "-";
          return `${apiDirection}${field}`;
        };
        const orderString = builderParams.orderParams
          .map(orderMapper, [])
          .join(",");
        query.push(`order=${orderString}`);
      }

      if (!!builderParams.page) {
        query.push(`page=${builderParams.page}`);
      }

      if (!!builderParams.perPage) {
        query.push(`count=${builderParams.perPage}`);
      }
    }

    let finalQuery;

    if (query.length === 0) {
      finalQuery = "";
    } else {
      finalQuery = `?${query.join("&")}`;
    }

    return `${builderParams.resource}${finalQuery}`;
  };

  resource = resource => {
    this.builderParams = { ...this.builderParams, resource };
    return this;
  };

  page = page => {
    this.builderParams = { ...this.builderParams, page };
    return this;
  };

  perPage = perPage => {
    this.builderParams = { ...this.builderParams, perPage };
    return this;
  };

  group = (field, transformations) => {
    this.builderParams = {
      ...this.builderParams,
      groupParams: {
        field,
        transformations: transformations.filter(
          ({ operation }) => operation in ApiResourceBuilder.GroupOPs
        )
      }
    };
  };

  order = (field, direction = ApiResourceBuilder.OrderOPs.ASC) => {
    if (direction in ApiResourceBuilder.OrderOPs) {
      this.builderParams = {
        ...this.builderParams,
        orderParams: [...this.builderParams.orderParams, { field, direction }]
      };
    }
    return this;
  };

  addFilter = (
    field,
    value,
    operation,
    connector = ApiResourceBuilder.ConnectionOPs.AND
  ) => {
    if (operation in ApiResourceBuilder.FilterOPs) {
      if (connector in ApiResourceBuilder.ConnectionOPs) {
        this.builderParams = {
          ...this.builderParams,
          filterParams: [
            ...this.builderParams.filterParams,
            { field, value, operation, connector }
          ]
        };
      }
    }
  };

  addTransformation = (operation, field) => {
    if (operation in ApiResourceBuilder.TransformOPs) {
      this.builderParams = {
        ...this.builderParams,
        transformationParams: [
          ...this.builderParams.transformationParams,
          { operation, field }
        ]
      };
    }
  };

  build = (additionalParams = []) => {
    return this.buildResource(this.builderParams, additionalParams);
  };
}

export default ApiResourceBuilder;



// WEBPACK FOOTER //
// ./src/core/builder/api-resource.builder.js